import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { appMonthNameYear } from '@/utils/appDateFormat';
import { WpsDueMonthView } from '../../../../models/payroll';

interface OverdueLinesProps {
  dueMonth: WpsDueMonthView | null | undefined;
  year: number;
  month: number;
  onOpen: (year: number, month: number) => void;
}

/**
 * A fixed line above the selected month for each open month not on screen, so browsing another month cannot hide a live
 * alert. One for the due month while it is open, one for each older open month. Also on the Compliance Center.
 */
export const OverdueLines: React.FC<OverdueLinesProps> = ({ dueMonth, year, month, onOpen }) => {
  const { t } = useTranslation();
  if (!dueMonth) return null;

  const lines = [
    ...(dueMonth.olderOpenMonths ?? []).map(m => ({ year: m.year, month: m.month, count: m.outstandingCount, older: true })),
    ...(dueMonth.open
      ? [{ year: dueMonth.year, month: dueMonth.month, count: dueMonth.outstandingCount, older: false }]
      : []),
  ].filter(line => !(line.year === year && line.month === month));

  if (lines.length === 0) return null;

  return (
    <ul className="space-y-1.5" data-testid="wps-overdue-lines">
      {lines.map(line => {
        const name = appMonthNameYear(new Date(line.year, line.month - 1));
        // The due month can be open with nobody outstanding: everyone was held back or the share is below the test.
        const text = (() => {
          if (line.count === 0) return t('payroll.wps.uae.overdue.needsAttention', { month: name });
          if (line.older) return t('payroll.wps.uae.overdue.older', { month: name, count: line.count });
          // Amber is the due date itself, and nothing is late yet.
          return dueMonth.zone === 'Amber'
            ? t('payroll.wps.uae.overdue.dueToday', { month: name, count: line.count })
            : t('payroll.wps.uae.overdue.due', { month: name, count: line.count });
        })();
        return (
          <li
            key={`${line.year}-${line.month}`}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
            data-testid="wps-overdue-line"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-500" aria-hidden="true" />
            <span className="min-w-0">{text}</span>
            <button
              type="button"
              className="font-medium underline underline-offset-2 hover:no-underline"
              onClick={() => onOpen(line.year, line.month)}
              data-testid="wps-overdue-open"
            >
              {t('payroll.wps.uae.overdue.open')}
            </button>
          </li>
        );
      })}
    </ul>
  );
};
